import { useAppDispatch, useAppSelector } from "@/redux/features/hooks";
import { pickCurrentFranchise } from "@/lib/franchise";
import { useLazyGetCurrentUserQuery } from "@/redux/services/user/user.api";
import {
  useLazyGetCurrentRestaurantQuery,
  useLazyGetMyFranchisesQuery,
} from "@/redux/services/restaurant/restaurant.api";
import {
  setCurrentUser,
  setCurrentRestaurant,
  switchFranchise,
} from "@/redux/features/user/user.slice";
import { Roles, User } from "@/redux/services/user/user.type";
import { Restaurant } from "@/redux/services/restaurant/restaurant.type";

export const useFetchCurrentUserAndRestaurant = () => {
  const dispatch = useAppDispatch();
  const [getCurrentUser] = useLazyGetCurrentUserQuery();
  const [getCurrentRestaurant] = useLazyGetCurrentRestaurantQuery();
  const [getMyFranchises] = useLazyGetMyFranchisesQuery();
  const selectedFranchiseId = useAppSelector(
    (state) => state.user.currentFranchise?.id
  );

  const fetchUserAndRestaurant = async () => {
    const resultUser = await getCurrentUser();
    let userData: User | null = null;

    if ("data" in resultUser) {
      userData = resultUser.data as User;
      dispatch(setCurrentUser(userData));
    } else {
      throw new Error("Failed to fetch user data");
    }

    if (resultUser.data?.roles?.includes(Roles.owner)) {
      // Owners work in one franchise at a time (SFRESTO-169): keep the one
      // selected on this device if they still own it, else the first one.
      // switchFranchise drops the current restaurant if the franchise changed.
      const resultFranchises = await getMyFranchises();
      const franchise =
        "data" in resultFranchises && resultFranchises.data
          ? pickCurrentFranchise(resultFranchises.data, selectedFranchiseId)
          : undefined;
      if (franchise) {
        dispatch(switchFranchise(franchise));
      } else {
        throw new Error("Failed to fetch restaurant data");
      }
    }

    const roles = [
      Roles.cashier,
      Roles.waiter,
      Roles.manager,
      Roles.support,
      Roles.kitchen,
      Roles.staff,
    ];
    if (roles.some((role) => resultUser.data?.roles?.includes(role))) {
      const resultRestaurant = await getCurrentRestaurant();
      if ("data" in resultRestaurant) {
        dispatch(setCurrentRestaurant(resultRestaurant.data as Restaurant));
      } else {
        throw new Error("Failed to fetch restaurant data");
      }
    }

    return userData;
  };

  return { fetchUserAndRestaurant };
};
